import React, { useState } from 'react';
import {
    getAuth,
    EmailAuthProvider,
    reauthenticateWithCredential,
    updatePassword,
} from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import {
    Typography, Paper, TextField, Button, Divider, Container, CircularProgress,
} from '@mui/material';
import { useAuth } from '../context/AuthContext';
import { useNotification } from '../context/NotificationContext';
import { functions } from '../firebase';
import PageLayout from '../components/PageLayout';

/**
 * Minimale wachtwoordlengte die Firebase Authentication afdwingt.
 */
const MIN_PASSWORD_LENGTH = 6;

function Account() {
    const { user, refreshUserData } = useAuth();
    const notify = useNotification();

    const [name, setName] = useState(user?.name || '');
    const [savingName, setSavingName] = useState(false);

    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [savingPassword, setSavingPassword] = useState(false);

    const trimmedName = name.trim();
    const nameChanged = trimmedName !== (user?.name || '');

    const handleSaveName = async (e) => {
        e.preventDefault();
        if (!trimmedName) {
            notify.error('Naam mag niet leeg zijn.');
            return;
        }
        if (!nameChanged) return;

        setSavingName(true);
        try {
            // De gebruikersnaam wordt op veel plekken in de database gedenormaliseerd
            // (deelnemers, ranglijsten, wedstrijdschema, historie). Een callable met
            // Admin-rechten werkt al die kopieën in één keer bij; de client mag die
            // documenten zelf niet schrijven (firestore.rules).
            const renameUser = httpsCallable(functions, 'renameUser');
            await renameUser({ newName: trimmedName });
            await refreshUserData();
            notify.success('Gebruikersnaam bijgewerkt.');
        } catch (err) {
            console.error('Fout bij het wijzigen van de gebruikersnaam:', err);
            if (err?.code === 'functions/already-exists') {
                notify.error(err.message || 'Deze naam is al in gebruik in een van je veilingen.');
            } else if (err?.code === 'functions/invalid-argument') {
                notify.error(err.message || 'Ongeldige naam.');
            } else {
                notify.error('Het wijzigen van de gebruikersnaam is mislukt.');
            }
        } finally {
            setSavingName(false);
        }
    };

    const handleChangePassword = async (e) => {
        e.preventDefault();
        if (!currentPassword) {
            notify.error('Vul je huidige wachtwoord in.');
            return;
        }
        if (newPassword.length < MIN_PASSWORD_LENGTH) {
            notify.error(`Het nieuwe wachtwoord moet minstens ${MIN_PASSWORD_LENGTH} tekens bevatten.`);
            return;
        }
        if (newPassword !== confirmPassword) {
            notify.error('De nieuwe wachtwoorden komen niet overeen.');
            return;
        }

        setSavingPassword(true);
        try {
            const auth = getAuth();
            const authUser = auth.currentUser;
            // Firebase eist een recente login voor wachtwoordwijziging; daarom eerst
            // opnieuw authenticeren met het huidige wachtwoord.
            const credential = EmailAuthProvider.credential(authUser.email, currentPassword);
            await reauthenticateWithCredential(authUser, credential);
            await updatePassword(authUser, newPassword);

            setCurrentPassword('');
            setNewPassword('');
            setConfirmPassword('');
            notify.success('Wachtwoord gewijzigd.');
        } catch (err) {
            console.error('Fout bij het wijzigen van het wachtwoord:', err);
            if (err?.code === 'auth/wrong-password' || err?.code === 'auth/invalid-credential') {
                notify.error('Je huidige wachtwoord is onjuist.');
            } else if (err?.code === 'auth/weak-password') {
                notify.error('Het nieuwe wachtwoord is te zwak.');
            } else if (err?.code === 'auth/too-many-requests') {
                notify.error('Te veel pogingen. Probeer het later opnieuw.');
            } else {
                notify.error('Het wijzigen van het wachtwoord is mislukt.');
            }
        } finally {
            setSavingPassword(false);
        }
    };

    return (
        <PageLayout>
            <Container component="main" maxWidth="sm" sx={{ py: 4 }}>
                <Typography variant="h4" gutterBottom>Mijn Account</Typography>

                <Paper component="form" onSubmit={handleSaveName} elevation={3} sx={{ p: 3, mb: 3 }}>
                    <Typography variant="h6" gutterBottom>Gegevens</Typography>
                    <TextField
                        label="E-mail"
                        value={user?.email || ''}
                        fullWidth
                        margin="normal"
                        disabled
                    />
                    <TextField
                        label="Gebruikersnaam"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        fullWidth
                        margin="normal"
                        required
                    />
                    <Button
                        type="submit"
                        variant="contained"
                        sx={{ mt: 2 }}
                        disabled={savingName || !nameChanged || !trimmedName}
                    >
                        {savingName ? <CircularProgress size={24} color="inherit" /> : 'Naam opslaan'}
                    </Button>
                </Paper>

                <Paper component="form" onSubmit={handleChangePassword} elevation={3} sx={{ p: 3 }}>
                    <Typography variant="h6" gutterBottom>Wachtwoord wijzigen</Typography>
                    <TextField
                        label="Huidig wachtwoord"
                        type="password"
                        value={currentPassword}
                        onChange={(e) => setCurrentPassword(e.target.value)}
                        fullWidth
                        margin="normal"
                        autoComplete="current-password"
                        required
                    />
                    <Divider sx={{ my: 1 }} />
                    <TextField
                        label="Nieuw wachtwoord"
                        type="password"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        fullWidth
                        margin="normal"
                        autoComplete="new-password"
                        required
                    />
                    <TextField
                        label="Bevestig nieuw wachtwoord"
                        type="password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        fullWidth
                        margin="normal"
                        autoComplete="new-password"
                        required
                    />
                    <Button
                        type="submit"
                        variant="contained"
                        sx={{ mt: 2 }}
                        disabled={savingPassword}
                    >
                        {savingPassword ? <CircularProgress size={24} color="inherit" /> : 'Wachtwoord wijzigen'}
                    </Button>
                </Paper>
            </Container>
        </PageLayout>
    );
}

export default Account;
